import { API } from 'api';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import fetchBaseQueryHeaders from 'libs/fetchBaseQueryHeaders';

export const fleetApi = createApi({
    reducerPath: 'fleetApi',
    baseQuery: fetchBaseQuery({
        prepareHeaders: fetchBaseQueryHeaders,
    }),

    tagTypes: ['Fleet', 'Fleets'],

    endpoints: (builder) => ({
        getFleets: builder.query<IFleet[], TFleetListRequestParams>({
            query: (body) => {
                return {
                    url: API.FLEETS.LIST(),
                    method: 'POST',
                    body,
                };
            },

            providesTags: (result) =>
                result ? [...result.map(({ name }) => ({ type: 'Fleet' as const, id: name })), 'Fleets'] : ['Fleets'],
        }),

        getProjectFleets: builder.query<IFleet[], { projectName: IProject['project_name']; includeImported?: boolean }>({
            query: ({ projectName, includeImported }) => {
                return {
                    url: API.PROJECTS.FLEETS(projectName),
                    method: 'POST',
                    ...(typeof includeImported === 'boolean'
                        ? {
                              body: {
                                  include_imported: includeImported,
                              },
                          }
                        : {}),
                };
            },

            providesTags: (result) =>
                result ? [...result.map(({ name }) => ({ type: 'Fleet' as const, id: name })), 'Fleets'] : ['Fleets'],
        }),

        getFleetDetails: builder.query<
            IFleet,
            { projectName: IProject['project_name']; fleetName?: IFleet['name']; fleetId?: IFleet['id'] }
        >({
            query: ({ projectName, fleetName, fleetId }) => {
                return {
                    url: API.PROJECTS.FLEETS_DETAILS(projectName),
                    method: 'POST',
                    body: {
                        name: fleetName,
                        id: fleetId,
                    },
                };
            },

            providesTags: (result) => (result ? [{ type: 'Fleet' as const, id: result.name }] : []),
        }),

        deleteFleet: builder.mutation<IFleet[], { projectName: IProject['project_name']; fleetNames: string[] }>({
            query: ({ projectName, fleetNames }) => {
                return {
                    url: API.PROJECTS.FLEETS_DELETE(projectName),
                    method: 'POST',
                    body: { names: fleetNames },
                };
            },

            invalidatesTags: ['Fleets'],
        }),

        applyFleet: builder.mutation<IFleet, IApplyFleetPlanRequestRequest & { projectName: IProject['project_name'] }>({
            query: ({ projectName, ...body }) => {
                return {
                    url: API.PROJECTS.FLEETS_APPLY(projectName),
                    method: 'POST',
                    body,
                };
            },

            invalidatesTags: ['Fleets'],
        }),
    }),
});

export const {
    useGetFleetsQuery,
    useLazyGetFleetsQuery,
    useGetProjectFleetsQuery,
    useLazyGetProjectFleetsQuery,
    useDeleteFleetMutation,
    useGetFleetDetailsQuery,
    useApplyFleetMutation,
} = fleetApi;
